import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { CapTable } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/CapTable/CapTable';
import { HoverContextProvider } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/HoverColumnContext';
import { useCapTablePageController } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/useCapTablePageController';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import {
	useTranslationCapTable,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex } from '@mantine/core';
import { useContext, useMemo } from 'react';

import { CapTablePageHeader } from './CapTablePageHeader/CapTablePageHeader';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useCapTableController } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/CapTable/useCapTableController.tsx';
import { generatePath } from 'react-router-dom';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { CapTableRail } from '@dealstack/shared/lib/generated';

export const CapTablePage = () => {
	const tCommonUi = useTranslationCommonUi();
	const tCapTable = useTranslationCapTable();

	const { deals, deal } = useContext(DealContext);
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const {
		showSummarized,
		valuesHeldType,
		tableView,
		valueDisplayMode,
		setValueDisplayMode,
		isDownloadingExcel,
		setIsDownloadingExcel,
		isDownloadingPdf,
		setIsDownloadingPdf,
		showPercentages,
		showRowsCheckboxes,
		expandRowsMode,
		setExpandRowsMode,
		tablePageRef,
		tableRef,
		tableWrapperRef,
		setValuesHeldType,
		setShowSummarized,
		setTableView,
		setShowRowsCheckboxes,
		isFundDeal,
		isMasterView,
		setIsMasterView,
		rail,
		setRail,
	} = useCapTablePageController();

	// CAP-1062: the feature flag keeps every nominee surface dark. With the flag off the rail
	// toggle is hidden and the query always requests the beneficial (single-rail) projection,
	// even if a previously stored setting says otherwise.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	const effectiveRail = nomineeStructuresEnabled ? rail : CapTableRail.BENEFICIAL;

	const capTableController = useCapTableController({
		dealId: deal?.id,
		showSummarized,
		valuesHeldType,
		tableView,
		showPercentages,
		showRowsCheckboxes,
		expandRowsMode,
		setExpandRowsMode,
		isMasterView,
		fundId: isFundDeal && isMasterView ? deal?.fundId : undefined,
		rail: effectiveRail,
	});

	const breadcrumbs = useMemo(() => {
		const baseBreadcrumbs = makeDealPageBreadcrumbs({
			tCommonUi,
			dealId: deal?.id,
			dealName: deal?.name,
			deals,
			onlyDealDropdown: inPageFeatureFlag,
		});
		if (inPageFeatureFlag) {
			return baseBreadcrumbs;
		} else {
			return [
				...baseBreadcrumbs,
				{
					name: tCapTable('title'),
					url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_TABLE}`, {
						dealId: deal?.id ?? '',
					}),
				},
			];
		}
	}, [inPageFeatureFlag, deal, deals, tCapTable, tCommonUi]);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	return (
		<Flex direction="column" w="100%" h="100%" p="1rem 2rem" gap="1rem">
			<BreadcrumbsProvider>
				<CapTablePageHeader
					dealName={deal?.name || ''}
					dealId={deal?.id || ''}
					tableAllRowsLength={capTableController.table.getRowModel().rows.length}
					tableAllColumnsLength={capTableController.table.getAllColumns().length}
					tableWrapperRef={tableWrapperRef}
					tableRef={tableRef}
					setIsDownloadingPdf={setIsDownloadingPdf}
					setIsDownloadingExcel={setIsDownloadingExcel}
					valuesHeldType={valuesHeldType}
					setValueHeldType={setValuesHeldType}
					showSummarized={showSummarized}
					setShowSummarized={setShowSummarized}
					valueDisplayMode={valueDisplayMode}
					setValueDisplayMode={setValueDisplayMode}
					tableView={tableView}
					setTableView={setTableView}
					showPercentages={showPercentages}
					setShowRowsCheckboxes={setShowRowsCheckboxes}
					showRowsCheckboxes={showRowsCheckboxes}
					isFundDeal={isFundDeal}
					isMasterView={isMasterView}
					setIsMasterView={setIsMasterView}
					rail={effectiveRail}
					setRail={setRail}
					hasNomineeStructure={
						nomineeStructuresEnabled &&
						(capTableController.capTableData?.nomineeFlags?.hasNomineeStructure ??
							false)
					}
				/>

				<HoverContextProvider>
					<CapTable
						table={capTableController.table}
						isLoadingData={capTableController.isLoading}
						isExportingData={isDownloadingExcel || isDownloadingPdf}
						isTableEmpty={
							!capTableController.isLoading &&
							!capTableController.capTableData?.issuers?.length
						}
						tablePageRef={tablePageRef}
						tableWrapperRef={tableWrapperRef}
						tableRef={tableRef}
					/>
				</HoverContextProvider>
			</BreadcrumbsProvider>
		</Flex>
	);
};
